/**
 * 10.9BY — TELA DE SKILLS + TREINO. Abre pelo Boneco de Treino (hub, leste) e pelo painel do
 * Herói. Lista as 7 skills (nível, barra de XP, bônus atual); escolhe uma skill + duração e
 * contrata treino OFFLINE (🪙/h ou 💎). Com treino em andamento/terminado: Coletar (opcional
 * 📺 Dobrar) e "Terminar agora por 💎". Só desenha — quem decide/paga é o Game (handlers).
 */
import { i18n } from "../i18n/i18n";
import { confirmBuy } from "./ConfirmBuy"; // 10.9ET
import { fmtDuration, fmtNum } from "../utils/format";
import type { SkillId } from "../progression/Skills";

export interface TrainingSkillRow {
  id: SkillId;
  icon: string;
  name: string;
  level: number;
  xp: number;
  xpNext: number; // 0 = nível máximo
  bonus: string; // "+16% dano · +4% vel." / "-8% dano recebido"
  costPerHour: number; // 🪙/h se treinar esta
  ratePerMin: number; // XP/min se treinar esta
  current: boolean; // é a skill da arma em uso (destaque)
}

export interface TrainingActive {
  skill: SkillId;
  name: string;
  icon: string;
  startedAt: number;
  endsAt: number;
  ratePerMin: number;
  pendingXp: number;
  finishCost: number; // 💎 pra terminar agora (0 = já terminou)
  done: boolean;
}

export interface TrainingView {
  skills: TrainingSkillRow[];
  training: TrainingActive | null;
  coins: number;
  diamonds: number;
  hours: number[]; // durações disponíveis (VIP inclui 24)
  /** oferta de dobrar a coleta: "ad" (vídeo) · "vip" (direto) · null (esgotou hoje) */
  adDouble: "ad" | "vip" | null;
}

export interface TrainingHandlers {
  onClose: () => void;
  onStart: (skill: SkillId, hours: number, payWith: "coins" | "diamonds") => void;
  onCollect: (double: boolean) => void;
  onFinishNow: () => void;
}

export class TrainingScreen {
  private readonly root: HTMLDivElement;
  private readonly body: HTMLElement;
  private getView: (() => TrainingView) | null = null;
  private selected: SkillId | null = null;
  private hours = 1;
  private tick: number | undefined;

  constructor(uiRoot: HTMLElement, private readonly h: TrainingHandlers) {
    injectStyles();
    this.root = document.createElement("div");
    this.root.className = "overlay train-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel void-panel train-panel">
        <div class="train-head">
          <h2 class="train-title"></h2>
          <button class="btn train-x" data-a="close">✕</button>
        </div>
        <div class="train-body"></div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.body = this.root.querySelector(".train-body")!;
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => this.h.onClose());
    this.root.addEventListener("click", (e) => { if (e.target === this.root) this.h.onClose(); });
    i18n.onChange(() => { if (!this.root.hidden) this.render(); });
  }

  open(getView: () => TrainingView, preselect?: SkillId | null): void {
    this.getView = getView;
    if (preselect) this.selected = preselect;
    this.root.hidden = false;
    this.render();
    // relógio do treino em andamento (tempo restante / XP rendido)
    if (this.tick === undefined) this.tick = window.setInterval(() => { if (!this.root.hidden) this.render(); }, 15000);
  }

  hide(): void {
    this.root.hidden = true;
    if (this.tick !== undefined) { window.clearInterval(this.tick); this.tick = undefined; }
  }

  refresh(): void { if (!this.root.hidden) this.render(); }

  private render(): void {
    if (!this.getView) return;
    const v = this.getView();
    this.root.querySelector(".train-title")!.textContent = i18n.t("skills.title");
    if (!this.selected) this.selected = v.skills.find((s) => s.current)?.id ?? v.skills[0].id;
    if (!v.hours.includes(this.hours)) this.hours = v.hours[0];

    const wallet = `<div class="train-wallet"><span>🪙 ${fmtNum(v.coins)}</span><span>💎 ${fmtNum(v.diamonds)}</span></div>`;

    const list = v.skills.map((s) => {
      const pct = s.xpNext > 0 ? Math.round((s.xp / s.xpNext) * 100) : 100;
      const sel = s.id === this.selected ? " sel" : "";
      const cur = s.current ? `<span class="ts-cur">${i18n.t("skills.inUse")}</span>` : "";
      return `<button class="train-skill${sel}" data-s="${s.id}">
        <span class="ts-ic">${s.icon}</span>
        <span class="ts-main">
          <span class="ts-top"><b class="ts-name">${s.name}</b>${cur}<span class="ts-lv">Nv ${s.level}</span></span>
          <span class="ts-bar"><i style="width:${pct}%"></i></span>
          <span class="ts-sub"><span>${s.bonus}</span><span>${s.xpNext > 0 ? `${fmtNum(s.xp)}/${fmtNum(s.xpNext)} XP` : i18n.t("skills.max")}</span></span>
        </span>
      </button>`;
    }).join("");

    let action: string;
    const t = v.training;
    if (t) {
      const left = Math.max(0, t.endsAt - Date.now());
      const status = t.done ? i18n.t("training.done") : i18n.t("training.left", { time: fmtDuration(left) });
      const dbl = v.adDouble && t.pendingXp > 0
        ? `<button class="btn conv" data-a="double">${i18n.t(v.adDouble === "vip" ? "training.doubleVip" : "training.double")}</button>`
        : "";
      const finish = !t.done && t.finishCost > 0
        ? `<button class="btn" data-a="finish">${i18n.t("training.finishNow", { n: t.finishCost })}</button>`
        : "";
      action = `<div class="train-active">
        <div class="ta-top"><span>${t.icon} ${i18n.t("training.training", { name: t.name })}</span><span class="ta-status">${status}</span></div>
        <div class="ta-line">${i18n.t("training.rate", { n: t.ratePerMin })} · ${i18n.t("training.pending", { n: fmtNum(t.pendingXp) })}</div>
        <div class="ta-btns">
          <button class="btn primary" data-a="collect" ${t.pendingXp > 0 || t.done ? "" : "disabled"}>${i18n.t("training.collect", { n: fmtNum(t.pendingXp) })}</button>
          ${dbl}${finish}
        </div>
      </div>`;
    } else {
      const s = v.skills.find((x) => x.id === this.selected)!;
      const coins = s.costPerHour * this.hours;
      const dia = Math.max(1, Math.ceil(coins / 100));
      const hoursBtns = v.hours.map((hh) => `<button class="train-h${hh === this.hours ? " active" : ""}" data-h="${hh}">${hh}h</button>`).join("");
      const canCoins = v.coins >= coins, canDia = v.diamonds >= dia;
      action = `<div class="train-start">
        <div class="ta-top"><span>${s.icon} ${i18n.t("training.train", { name: s.name })}</span><span class="ta-status">${i18n.t("training.rate", { n: s.ratePerMin })}</span></div>
        <div class="train-hours">${hoursBtns}</div>
        <div class="ta-line">${i18n.t("training.willGain", { n: fmtNum(s.ratePerMin * this.hours * 60) })} · ${i18n.t("training.perHour", { n: s.costPerHour })}</div>
        <div class="ta-btns">
          <button class="btn primary" data-a="start-coins" ${canCoins ? "" : "disabled"}>${i18n.t("training.payCoins", { n: fmtNum(coins) })}</button>
          <button class="btn conv" data-a="start-dia" ${canDia ? "" : "disabled"}>${i18n.t("training.payDiamonds", { n: dia })}</button>
        </div>
      </div>`;
    }

    this.body.innerHTML = wallet + `<div class="train-list">${list}</div>` + action + `<div class="train-hint">${i18n.t("skills.hint")}</div>`;

    this.body.querySelectorAll<HTMLButtonElement>(".train-skill").forEach((b) => {
      b.onclick = () => { this.selected = b.dataset.s as SkillId; this.render(); };
    });
    this.body.querySelectorAll<HTMLButtonElement>(".train-h").forEach((b) => {
      b.onclick = () => { this.hours = Number(b.dataset.h); this.render(); };
    });
    const on = (a: string, fn: () => void): void => { this.body.querySelector<HTMLButtonElement>(`[data-a="${a}"]`)?.addEventListener("click", fn); };
    // 10.9ET — contratar treino tambem pede confirmacao (antes gastava no toque)
    const ask = (a: string, fn: () => void): void => {
      const b = this.body.querySelector<HTMLButtonElement>(`[data-a="${a}"]`);
      if (!b) return;
      const panel = (this.body.closest(".overlay") as HTMLElement | null) ?? this.body;
      b.addEventListener("click", () => confirmBuy(panel, i18n.t("training.confirm", { what: b.textContent?.trim() ?? "" }), fn));
    };
    ask("start-coins", () => { if (this.selected) this.h.onStart(this.selected, this.hours, "coins"); });
    ask("start-dia", () => { if (this.selected) this.h.onStart(this.selected, this.hours, "diamonds"); });
    on("collect", () => this.h.onCollect(false));
    on("double", () => this.h.onCollect(true));
    ask("finish", () => this.h.onFinishNow());
  }
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  stylesInjected = true;
  const css = `
  .train-panel{max-width:460px;width:94%;max-height:88vh;display:flex;flex-direction:column;gap:10px;text-align:left}
  .train-head{display:flex;align-items:center;justify-content:space-between}
  .train-title{margin:0;font-size:20px;color:#eaf1ff}
  .train-x{padding:6px 12px}
  .train-body{flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;display:flex;flex-direction:column;gap:10px}
  .train-wallet{display:flex;gap:14px;justify-content:flex-end;font-weight:800;color:#ffd54a;font-size:13px}
  .train-list{display:flex;flex-direction:column;gap:6px}
  .train-skill{display:flex;gap:10px;align-items:center;width:100%;text-align:left;background:rgba(8,10,22,.55);border:1px solid rgba(124,77,255,.2);border-radius:12px;padding:8px 10px;color:#eaf1ff;cursor:pointer;font-family:inherit}
  .train-skill.sel{border-color:#ffb020;background:rgba(255,176,32,.12);box-shadow:0 0 12px rgba(255,176,32,.2)}
  .ts-ic{font-size:22px;width:30px;text-align:center;flex-shrink:0}
  .ts-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:4px}
  .ts-top{display:flex;align-items:center;gap:8px}
  .ts-name{font-size:14px}
  .ts-cur{font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:#2be0ff;border:1px solid rgba(43,224,255,.5);border-radius:6px;padding:1px 5px}
  .ts-lv{margin-left:auto;font-weight:800;color:#ffd54a;font-size:13px}
  .ts-bar{display:block;height:6px;border-radius:3px;background:rgba(255,255,255,.08);overflow:hidden}
  .ts-bar i{display:block;height:100%;background:linear-gradient(90deg,#7c4dff,#2be0ff);border-radius:3px}
  .ts-sub{display:flex;justify-content:space-between;gap:8px;font-size:11px;color:#9fb4ff}
  .train-active,.train-start{background:rgba(255,176,32,.08);border:1px solid rgba(255,176,32,.35);border-radius:12px;padding:10px 12px;display:flex;flex-direction:column;gap:8px}
  .ta-top{display:flex;justify-content:space-between;align-items:baseline;gap:8px;font-weight:800;font-size:14px;color:#eaf1ff}
  .ta-status{font-size:12px;color:#ffd54a;font-weight:700;flex-shrink:0}
  .ta-line{font-size:12px;color:#c9d4f0}
  .ta-btns{display:flex;flex-wrap:wrap;gap:8px}
  .ta-btns .btn{flex:1;min-width:120px;padding:9px 10px;font-size:13px}
  .ta-btns .btn:disabled{opacity:.45;cursor:default}
  .train-hours{display:flex;gap:6px}
  .train-h{flex:1;background:rgba(8,10,22,.6);border:1px solid rgba(255,176,32,.3);border-radius:9px;color:#e6d9a8;font:700 13px system-ui,sans-serif;padding:8px 4px;cursor:pointer}
  .train-h.active{background:#ffb020;color:#1a1200;border-color:#ffb020}
  .train-hint{font-size:11px;line-height:1.45;color:#9aa7d6}`;
  const el = document.createElement("style");
  el.textContent = css;
  document.head.appendChild(el);
}
